// Global utility functions for VoxSmyth Enhanced Platform

// Scroll to top utility function
export const scrollToTop = (options = {}) => {
  const {
    behavior = 'smooth',
    top = 0,
    left = 0
  } = options;
  
  if ('scrollBehavior' in document.documentElement.style) {
    window.scrollTo({
      top,
      left,
      behavior
    });
  } else {
    // Fallback for older browsers
    window.scrollTo(top, left);
  }
};

// Smooth scroll to element utility
export const scrollToElement = (elementId, options = {}) => {
  const element = document.getElementById(elementId);
  if (element) {
    const {
      behavior = 'smooth',
      block = 'start',
      inline = 'nearest'
    } = options;
    
    element.scrollIntoView({
      behavior,
      block,
      inline
    });
  }
};

// Copy to clipboard utility
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        return { success: true };
      } catch (err) {
        return { success: false, error: err };
      } finally {
        document.body.removeChild(textArea);
      }
    }
  } catch (error) {
    return { success: false, error };
  }
};

// Format time utility for audio players
export const formatTime = (timeInSeconds) => {
  if (isNaN(timeInSeconds) || timeInSeconds < 0) return '00:00';
  
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};

// Debounce utility for search inputs
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Theme utilities
export const getVoxSmythTheme = () => ({
  colors: {
    primary_bg: '#2C3A4C',
    secondary_bg: '#4A5568',
    accent_brass: '#D7B377',
    warm_parchment: '#F7F3E9',
    light_parchment: '#F9F6F0',
    deep_slate: '#1A202C',
    pure_white: '#FFFFFF'
  },
  typography: {
    font_serif: 'DM Serif Display, Georgia, serif',
    font_sans: 'DM Sans, sans-serif',
    font_inter: 'Inter, sans-serif'
  }
});

// Animation utilities
export const fadeIn = (element, duration = 300) => {
  element.style.opacity = '0';
  element.style.display = 'block';
  
  const start = performance.now();
  
  const animate = (timestamp) => {
    const elapsed = timestamp - start;
    const progress = Math.min(elapsed / duration, 1);
    
    element.style.opacity = progress;
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };
  
  requestAnimationFrame(animate);
};

export const fadeOut = (element, duration = 300, callback = null) => {
  element.style.opacity = '1';
  
  const start = performance.now();
  
  const animate = (timestamp) => {
    const elapsed = timestamp - start;
    const progress = Math.min(elapsed / duration, 1);
    
    element.style.opacity = 1 - progress;
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      element.style.display = 'none';
      if (callback) callback();
    }
  };
  
  requestAnimationFrame(animate);
};

// Local storage utilities
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

// URL utilities
export const generateShareUrl = (path = '', params = {}) => {
  const baseUrl = window.location.origin;
  const url = new URL(`${baseUrl}${path}`);
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.append(key, value);
    }
  });
  
  return url.toString();
};

// Device detection
export const isMobile = () => {
  return window.innerWidth <= 768;
};

export const isTablet = () => {
  return window.innerWidth > 768 && window.innerWidth <= 1024;
};

export const isDesktop = () => {
  return window.innerWidth > 1024;
};

// Toast notification utility
export const showToast = (message, type = 'success', duration = 3000) => {
  const theme = getVoxSmythTheme();
  
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    top: 24px;
    right: 24px;
    background: ${type === 'success' ? theme.colors.warm_parchment : '#FEE2E2'};
    border: 1px solid ${type === 'success' ? theme.colors.accent_brass : '#EF4444'};
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 8px 24px rgba(44, 58, 76, 0.15);
    z-index: 10000;
    font-family: ${theme.typography.font_sans};
    color: ${theme.colors.primary_bg};
    max-width: 400px;
    transform: translateX(400px);
    transition: transform 0.3s ease-out;
  `;
  
  toast.innerHTML = `
    <div style="display: flex; align-items: center; gap: 8px;">
      <span style="font-size: 16px;">${type === 'success' ? '✨' : '⚠️'}</span>
      <span>${message}</span>
    </div>
  `;
  
  document.body.appendChild(toast);
  
  // Slide in
  setTimeout(() => {
    toast.style.transform = 'translateX(0)';
  }, 100);
  
  // Slide out and remove
  setTimeout(() => {
    toast.style.transform = 'translateX(400px)';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }, duration);
};

// Analytics utilities
export const trackEvent = (eventName, properties = {}) => {
  // Simple console logging for development
  console.log('Event tracked:', eventName, properties);
  
  // Future integration point for analytics service
  // Can be extended later with preferred analytics solution
};

// Export all utilities as default
const VoxSmythUtils = {
  scrollToTop,
  scrollToElement,
  copyToClipboard,
  formatTime,
  debounce,
  getVoxSmythTheme,
  fadeIn,
  fadeOut,
  setLocalStorage,
  getLocalStorage,
  generateShareUrl,
  isMobile,
  isTablet,
  isDesktop,
  showToast,
  trackEvent
};

export default VoxSmythUtils;